import { useAppSelector } from '@/Application/Hooks/Redux';
import { TPaymentMethodId } from '@/modules/perseo-billing/components/accordion/interfaces';
import type {
  IOrderConfirmation,
  IPaymentMethod
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import type { IconTypes } from '@/modules/perseo-billing/ui/react/src';
import {
  Divider,
  Icon,
  Typography,
  useMediaQuery
} from '@/modules/perseo-billing/ui/react/src';

import { ECencoPayProcessorSubType, type PaymentDetailProps } from '..';

const PriceDetails = () => {
  const isMobile = useMediaQuery('lg');
  const billing = useAppSelector((state) => state.billing);
  const paymentSuccess = billing.paymentSuccess as IOrderConfirmation;
  const totalSummary = paymentSuccess?.totalSummary;
  const {
    isCencoPrice,
    subTotalPrice,
    totalDeliveryPrice,
    totalCouponPrice,
    totalPriceGiftCard,
    totalPrice,
    wasPaidOnlyWithGiftCard
  } = totalSummary?.summary || {};

  const isCencoPay = paymentSuccess?.isCencoPay;

  const subTotalPaymentDetails: PaymentDetailProps[] = [];
  let totalPaymentDetail: PaymentDetailProps | undefined;

  const paymentMethodWithoutGiftcard = totalSummary?.paymentMethod?.filter(
    (method) => method?.type !== 'GIFT_CARD' || wasPaidOnlyWithGiftCard
  );

  if (paymentMethodWithoutGiftcard?.length) {
    const { bankCardType, type } =
      paymentMethodWithoutGiftcard[0] as IPaymentMethod;

    const icons = getPaymentSummaryIcons({
      wasPaidWithCencoPay: isCencoPay ?? false,
      // * 2023-01-22 this is the only way to know if the payment was made with a CAT card w/ and w/o CencoPay.
      wasPaidWithCAT:
        isCencoPrice && isCencoPay
          ? bankCardType === ECencoPayProcessorSubType.CencoPayCat
          : isCencoPrice && type === TPaymentMethodId.EPAY_CENCOSUD_TCOM,
      wasPaidWithGiftCard: wasPaidOnlyWithGiftCard
    });

    subTotalPaymentDetails.push({
      label: 'Subtotal:',
      labelWeight: 'medium',
      labelSize: 'h4',
      amount: subTotalPrice ?? '',
      primaryIcon: icons.primaryIcon,
      secondaryIcon: icons.secondaryIcon
    });

    totalPaymentDetail = {
      label: 'Total:',
      labelWeight: 'semibold',
      labelSize: 'h4',
      amount: totalPrice ?? '',
      primaryIcon: icons.primaryIcon,
      secondaryIcon: icons.secondaryIcon
    };
  } else {
    totalPaymentDetail = {
      label: 'Total:',
      labelWeight: 'semibold',
      labelSize: 'h4',
      amount: totalPrice ?? ''
    };
  }

  if (totalDeliveryPrice) {
    subTotalPaymentDetails.push({
      label: 'Costo despacho:',
      amount: totalDeliveryPrice
    });
  }

  if (totalCouponPrice && totalCouponPrice !== '$0') {
    subTotalPaymentDetails.push({
      label: isMobile
        ? 'Dcto cupón promocional:'
        : 'Descuento cupón promocional:',
      amount: totalCouponPrice,
      primaryIcon: 'AppliedDiscountCouponIcon',
      color: '#1245D6'
    });
  }

  const wasPaidWithGiftCard = totalSummary?.paymentMethod?.some(
    (method) => method?.type === 'GIFT_CARD'
  );

  if (
    !wasPaidOnlyWithGiftCard &&
    wasPaidWithGiftCard &&
    totalPriceGiftCard !== '$0'
  ) {
    subTotalPaymentDetails.push({
      label: 'Gift card aplicada:',
      amount: totalPriceGiftCard ?? '',
      primaryIcon: 'CardGiftCardIcon'
    });
  }

  return (
    <div className="co-w-full co-flex co-flex-col co-justify-center co-py-8px co-px-16px co-gap-16px co-rounded-4px co-bg-pds-neutral-100 lg:co-w-2/3">
      <div tabIndex={0} className="co-flex co-flex-col co-gap-8px">
        {subTotalPaymentDetails?.map((detail, index) => (
          <PaymentDetail key={index} {...detail} />
        ))}
      </div>
      <Divider />
      <div tabIndex={0}>
        <PaymentDetail {...totalPaymentDetail} />
      </div>
    </div>
  );
};

const PaymentDetail = ({
  label,
  labelWeight,
  labelSize,
  amount,
  color,
  primaryIcon,
  secondaryIcon
}: PaymentDetailProps) => {
  return (
    <div className="co-flex co-justify-between">
      <Typography size={labelSize} weight={labelWeight}>
        {label}
      </Typography>
      <div className="co-flex co-gap-8px">
        <div className="co-flex co-gap-8px co-flex-row-reverse">
          {primaryIcon && (
            <Icon icon={primaryIcon} className="co-w-24px co-h-24px" />
          )}
          {secondaryIcon && (
            <Icon icon={secondaryIcon} className="co-w-24px co-h-24px" />
          )}
        </div>
        <Typography
          weight="medium"
          size={labelSize}
          className={`co-text-[${color}]`}
        >
          {amount}
        </Typography>
      </div>
    </div>
  );
};

interface PaymentDetailIcons {
  wasPaidWithCencoPay: boolean;
  wasPaidWithCAT: boolean;
  wasPaidWithGiftCard?: boolean;
}
interface PaymentDetailSummaryIcons {
  primaryIcon?: IconTypes;
  secondaryIcon?: IconTypes;
}
const getPaymentSummaryIcons = ({
  wasPaidWithCencoPay,
  wasPaidWithCAT,
  wasPaidWithGiftCard
}: PaymentDetailIcons): PaymentDetailSummaryIcons => {
  const icons: { primaryIcon?: IconTypes; secondaryIcon?: IconTypes } = {};

  if (wasPaidWithCencoPay) {
    icons.primaryIcon = 'CencoPayIcon';
    if (wasPaidWithCAT) icons.secondaryIcon = 'CencoCreditCardIcon';
  } else if (wasPaidWithCAT) {
    icons.primaryIcon = 'CencoCreditCardIcon';
  } else if (wasPaidWithGiftCard) {
    icons.primaryIcon = 'CardGiftCardIcon';
  }

  return icons;
};

export default PriceDetails;
